/**
 * Explore View Component
 *
 * Displays assets from external providers (Freesound, PolyHaven).
 * Similar structure to HomeView but focused on exploring free/open-source assets.
 */

import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import type { IAsset, ProviderRegistry } from '../providers';
import { useMediaAssets, useAssetSelection } from '../hooks';
import type { UseMediaAssetsParams } from '../hooks';
import { MediaSidebar } from './MediaSidebar';
import { MediaHeader } from './MediaHeader';
import { AssetDetailPanel } from './AssetDetailPanel';
import type { Filters, AssetSource } from '../types';
import { MediaType } from '../types';
import type { AssetSelectCallback, SortOption } from '../types';
import { ProcessingIcon } from '../../../icons';
import { Dropzone } from './Dropzone';
import { MediaTabs } from './MediaTabs';
import { AssetRow } from './AssetRow';
import { extractUniqueCategoriesFromAssets } from '../utils';
import { useAssetMetadata } from '../hooks';
import { MediaGrid } from './MediaGrid';
import { InfiniteScrollContainer } from './InfiniteScrollContainer';
import { EmptyStateSection } from './EmptyStateSection';
import { DragSelectContainer } from './DragSelectContainer';
import { ZprAccordion, ZprAccordionPanel } from '@zpr-stencil';

interface CategoryScrollSectionProps {
  mediaAssetsParams: Omit<UseMediaAssetsParams, 'filters'> & { filters: Filters };
  category: string;
  onAssetClick: (asset: IAsset) => void;
  onAssetSelect: (assetId: string) => void;
  selectedAssets: string[];
  dragHighlightedIds: string[];
  isDragActive: boolean;
  onAssetsChange?: (category: string, assets: IAsset[]) => void;
}

const CategoryScrollSection = ({
  mediaAssetsParams,
  category,
  onAssetClick,
  onAssetSelect,
  selectedAssets,
  dragHighlightedIds,
  isDragActive,
  onAssetsChange,
}: CategoryScrollSectionProps) => {
  const { filters: baseFilters } = mediaAssetsParams;

  // Memoize using primitive deps so a new filters object reference from the
  // parent doesn't trigger the useMediaAssets reset effect on every render.
  const categoryFilters = useMemo<Filters>(() => ({
    ...baseFilters,
    categories: [category],
  }), [
    baseFilters.animated?.join('\0'),
    baseFilters.templateType?.join('\0'),
    baseFilters.status?.join('\0'),
    baseFilters.labels?.join('\0'),
    category,
  ]);

  const { assets, loading, loadingMore, hasMore, loadMore, error } = useMediaAssets({
    ...mediaAssetsParams,
    filters: categoryFilters,
  });

  useEffect(() => {
    onAssetsChange?.(category, assets);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assets, category, onAssetsChange]);

  if (loading) {
    return <div className="library-view__loading"><ProcessingIcon /></div>;
  }

  return (
    <InfiniteScrollContainer
      onLoadMore={loadMore}
      hasMore={hasMore}
      loadingMore={loadingMore}
      loader={<div className="library-view__loading"><ProcessingIcon /></div>}
      error={!!error}
    >
      <MediaGrid
        assets={assets}
        onAssetClick={onAssetClick}
        onAssetSelect={onAssetSelect}
        selectedAssets={selectedAssets}
        dragHighlightedIds={dragHighlightedIds}
        isDragActive={isDragActive}
      />
    </InfiniteScrollContainer>
  );
};

export interface ExploreViewProps {
  /** Provider registry containing external providers */
  registry: ProviderRegistry;
  /** Workspace ID for filtering ZML assets */
  workspaceId?: string;
  /** Available media types to filter by */
  enabledMediaTypes?: MediaType[];
  /** Current media type being browsed */
  mediaType?: MediaType | null;
  /** Enable multi-select mode for assets */
  enableMultiSelect?: boolean;
  /** Whether to show the multi-select toggle in the header */
  showMultiSelectToggle?: boolean;
  /** Callback to toggle multi-select mode */
  onToggleMultiSelect?: () => void;
  /** Callback when an asset is selected */
  onSelect?: AssetSelectCallback;
  /** Callback for back navigation */
  onBack?: () => void;
  /** Callback for handling file uploads */
  handleUpload?: (files: FileList) => void;
  /** Search query from parent */
  searchQuery?: string;
  /** Callback when search query changes */
  onSearchChange: (query: string) => void;
  /** Sort option from parent */
  sortBy: SortOption;
  /** Callback when sort changes */
  onSortChange?: (sort: SortOption) => void;
  /** Filters from parent */
  filters?: Filters;
  /** Callback when filters change */
  onFiltersChange?: (filters: Filters) => void;
  /** Callback to delete a single asset */
  onDelete?: (assetId: string, asset: IAsset) => Promise<boolean>;
  /** Callback to delete multiple assets */
  onDeleteMultiple?: (assetIds: string[]) => Promise<boolean>;
  /** Callback for source selection */
  onSourceSelect?: (source: AssetSource) => void;
  onOpenPremiumUploadModal?: () => void;
  /** Callback to handle asset editing */
  onEdit?: (asset: IAsset) => void;
  /** Changing this value triggers a re-fetch of assets */
  refreshKey?: number;
  /** When going back from the Asset View, this provider will be initially active */
  initialActiveProvider?: AssetSource;
  /** Custom CSS class */
  className?: string;
}

export const ExploreView = ({
  registry,
  enabledMediaTypes,
  mediaType,
  workspaceId,
  enableMultiSelect = true,
  showMultiSelectToggle = false,
  onToggleMultiSelect,
  onSelect,
  onBack,
  handleUpload,
  searchQuery = '',
  onSearchChange,
  sortBy: sortByProp,
  onSortChange,
  filters: filtersProp,
  onFiltersChange,
  onDelete,
  onDeleteMultiple,
  onSourceSelect,
  onOpenPremiumUploadModal,
  onEdit,
  className = '',
  refreshKey,
  initialActiveProvider,
}: ExploreViewProps) => {
  const sortBy = sortByProp;
  const defaultFilters = useMemo<Filters>(() => ({ animated: [], templateType: [], status: [], labels: [], categories: [] }), []);
  const filters: Filters = filtersProp ?? defaultFilters;

  const [activeSource, setActiveSource] = useState<AssetSource>('');
  const [activeProvider, setActiveProvider] = useState<AssetSource>(initialActiveProvider === 'zappar' ? 'zappar-library' : initialActiveProvider || '');
  const [filterMediaType, setFilterMediaType] = useState<MediaType | null>(mediaType || null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [openedCategories, setOpenedCategories] = useState<Set<string>>(new Set());
  const [categories, setCategories] = useState<string[]>([]);
  const [categoryAssetsMap, setCategoryAssetsMap] = useState<Record<string, IAsset[]>>({});
  const containerRef = useRef<HTMLDivElement>(null);

  const handleCategoryAssetsChange = useCallback((cat: string, newAssets: IAsset[]) => {
    setCategoryAssetsMap(prev => {
      if (prev[cat] === newAssets) return prev;
      return { ...prev, [cat]: newAssets };
    });
  }, []);

  // Get all providers
  const externalProviders = registry.getAll()

  // Load assets from each external provider
  const providerAssets = externalProviders.map(provider => {
    const providerId = provider.getId();
    const providerSource = providerId === 'zapworks' ? 'zappar-library' : providerId; // Map ZapworksProvider to 'zappar-library' source
    // Only fetch when viewing the overview (no activeProvider) or when this is the active provider.
    const isEnabled = !activeProvider || providerSource === activeProvider;

    const { assets, loading, loadingMore, hasMore, loadMore, error } = useMediaAssets({
      registry,
      mediaType: filterMediaType || enabledMediaTypes || undefined,
      source: providerSource,
      searchQuery,
      sortBy,
      filters,
      refreshKey,
      enabled: isEnabled,
    });

    return {
      providerId,
      providerName: provider.getDisplayName(),
      state: {
        assets,
        count: assets.length,
        loading,
        loadingMore,
        hasMore,
        loadMore,
        error
      }
    };
  });

  // Collect all assets from all providers for detail panel
  const allAssets = providerAssets.flatMap(row => row.state.assets);

  // When category accordion sections are visible, include their assets so
  // useAssetSelection can cache them and compute counts correctly.
  const visibleAssets = useMemo(() => {
    const categoryAssets = Object.values(categoryAssetsMap).flat();
    if (categoryAssets.length > 0) {
      // Merge: prefer category assets (they're what the user sees), supplement with provider assets
      const seen = new Set(categoryAssets.map(a => a.getId()));
      return [...categoryAssets, ...allAssets.filter(a => !seen.has(a.getId()))];
    }
    return allAssets;
  }, [allAssets, categoryAssetsMap]);

  const {
    selectedAssetIds,
    selectedAssets: selectedAssetObjects,
    hiddenSelectedCount,
    toggleSelection,
    addToSelection,
    clearSelection,
  } = useAssetSelection<IAsset>({
    enableMultiSelect,
    visibleAssets,
  });

  // Extract unique categories from all assets, or use provider's predefined list if available
  useEffect(() => {
    const fetchCategories = async () => {
      const providerSource = activeProvider === 'zappar-library' ? 'zapworks' : activeProvider;
      const provider = registry.get(providerSource);
      // Providers that support server-side category filtering (e.g. Pixabay) expose a
      // static category list via getCategories() rather than embedding categories in metadata.
      if (provider?.getCategories) {
        const result = await provider.getCategories({ types: filterMediaType ? [filterMediaType] : undefined });
        setCategories([...new Set(result)]);
      } else {
        setCategories(extractUniqueCategoriesFromAssets(providerAssets.filter(asset => (asset.providerId === providerSource)).flatMap(row => row.state.assets)));
      }
    };
    fetchCategories();
  }, [activeProvider, registry]);

  const activeProviderState = useMemo(() => {
    const providerSource = activeProvider === 'zappar-library' ? 'zapworks' : activeProvider;
    return providerAssets.find(row => row.providerId === providerSource)?.state;
  }, [activeProvider, providerAssets]);

  // Check loading state
  const isLoading = providerAssets.some(row => row.state.loading);

  // Check if any assets exist
  const hasAssets = providerAssets.some(row => row.state.assets.length > 0);


  // Handle search changes and propagate to parent
  const handleSearchChange = (query: string) => {
    onSearchChange(query);
  };

  const handleDeleteAsset = async (assetId: string, asset: IAsset) => {
    const success = await onDelete?.(assetId, asset);
    if (success) clearSelection();
  };

  const handleDeleteMultiple = async (assetIds: string[]) => {
    const success = await onDeleteMultiple?.(assetIds);
    if (success) clearSelection();
  };

  const handleAssetClick = (asset: IAsset) => {
    if (onSelect) {
      onSelect(asset);
    }
  };

  const { hasAnimated: computedShowAnimatedFilter } = useAssetMetadata(
    providerAssets.flatMap(p => p.state.assets)
  );

  const handleSortChange = (sort: SortOption) => {
    onSortChange?.(sort);
  };

  const handleFiltersChange = (newFilters: Filters) => {
    onFiltersChange?.(newFilters);
  };

  const handleSourceChange = (source: AssetSource) => {
    resetView();
    if (source !== 'explore') {
      setActiveSource(source);
      onSourceSelect?.(source);
    }
  };

  const resetView = () => {
    setFilterMediaType(null);
    setActiveSource('');
    setActiveProvider('');
    setSelectedCategory(null);
    setOpenedCategories(new Set());
    setCategoryAssetsMap({});
    onFiltersChange?.({
      ...filters,
      categories: [],
    });
  }

  const handleViewAll = (providerId: string) => {
    const provider = registry.get(providerId);
    if (provider) {
      const source: AssetSource = providerId === 'zapworks' ? 'zappar-library' : providerId;
      setActiveProvider(source);
      clearSelection();
    }
  };

  const handleExploreClick = () => {
    const selectedFilterMediaType = filterMediaType;
    resetView();
    clearSelection();
    if (selectedFilterMediaType) {
      setFilterMediaType(selectedFilterMediaType);
    }
  }

  const renderBreadcrumb = () => {
    if (activeProvider) {
      if (selectedCategory) {
        return (
          <p className="library-view__title--breadcrumb m0">
            <button onClick={handleExploreClick} className="btn btn-small btn-link">Explore {filterMediaType || 'all'} assets</button> / {providerDisplayName} / <b>{selectedCategory}</b>
          </p>
        )
      }
      return (
        <p className="library-view__title--breadcrumb m0">
          <button onClick={handleExploreClick} className="btn btn-small btn-link">Explore {filterMediaType || 'all'} assets</button> / <b>{ providerDisplayName }</b>
        </p>
      );
    }
  };

  const providerDisplayName = useMemo(() => {
    if (activeProvider) {
      const provider = registry.get(activeProvider === 'zappar-library' ? 'zapworks' : activeProvider);
      return provider?.getDisplayName() || activeProvider;
    }
    return '';
  }, [activeProvider, registry]);

  const handleCategorySelect = (category: string | null) => {
    setSelectedCategory(category);
    onFiltersChange?.({
      ...filters,
      categories: category ? [category] : [],
    });
  }

  return (
    <div className={`library-view library-view--with-sidebar ${className}`} ref={containerRef}>
      <div className="library-view__sidebar">
        <MediaSidebar
          registry={registry}
          mediaType={mediaType}
          showWorkspaceAssets={!!workspaceId}
          activeSource={activeSource || 'explore'}
          onSourceChange={handleSourceChange}
          onBack={onBack}
          isHomeView={false}
          showCategories={!!activeProvider}
          categories={categories}
          selectedCategory={selectedCategory}
          onCategorySelect={handleCategorySelect}
        />
      </div>

      <div className="library-view__main">
        <div className="library-view__header">
          <MediaHeader
            searchQuery={searchQuery}
            sortBy={sortBy}
            filters={filters}
            onSearchChange={handleSearchChange}
            onSortChange={handleSortChange}
            onFiltersChange={handleFiltersChange}
            onUpload={handleUpload}
            acceptedFileTypes={mediaType ? [mediaType] : enabledMediaTypes}
            showAnimatedFilter={computedShowAnimatedFilter}
            showMultiSelectToggle={showMultiSelectToggle}
            enableMultiSelect={enableMultiSelect}
            onToggleMultiSelect={onToggleMultiSelect}
            onOpenPremiumUploadModal={onOpenPremiumUploadModal}
            onBack={activeProvider ? handleExploreClick : onBack}
            currentViewTitle={activeProvider ? providerDisplayName : 'Explore assets'}
          />
        </div>


        {activeProvider ? (
          <div className="library-view__title">
            {renderBreadcrumb()}
          </div>
        ) : (
          !mediaType && enabledMediaTypes && enabledMediaTypes?.length > 1 && (
            <div className="library-view__tabs">
              <MediaTabs
                mediaTypes={enabledMediaTypes || Object.values(MediaType).filter(type => type !== MediaType.Unknown) as MediaType[]}
                activeType={filterMediaType}
                onTabChange={setFilterMediaType}
              />
            </div>
          )
        )}

        <DragSelectContainer enabled={enableMultiSelect} onSelect={toggleSelection} onSelectMany={addToSelection}>
          {({ dragHighlightedIds, isDragging }) => (
            <Dropzone onUpload={handleUpload}>
              <div className="library-view__content">
                {/* Empty State */}
                {!hasAssets && !isLoading ? (
                  <EmptyStateSection
                    sectionTitle={activeProvider ? activeProvider : 'Explore'}
                    tab={activeProvider ? `${selectedCategory}` : filterMediaType ? `${filterMediaType}` : ''}
                    searchQuery={searchQuery}
                  />
                ) :
                  isLoading ? (
                    <div className="library-view__loading">
                      <ProcessingIcon />
                    </div>
                  ) : (
                    <>
                      {/* If activeSource exists, show MediaGrid */}
                      {activeProvider ? (
                        <>
                        {/* For bigger screens, 840px(m2 breakpoints) and above */}
                        <InfiniteScrollContainer
                          className="m2:display-none"
                          onLoadMore={activeProviderState?.loadMore ?? (() => {})}
                          hasMore={activeProviderState?.hasMore ?? false}
                          loadingMore={activeProviderState?.loadingMore ?? false}
                          loader={<div className="library-view__loading"><ProcessingIcon /></div>}
                          error={!!activeProviderState?.error}
                        >
                          <MediaGrid
                            assets={activeProviderState?.assets || []}
                            onAssetClick={handleAssetClick}
                            onAssetSelect={toggleSelection}
                            selectedAssets={selectedAssetIds}
                            dragHighlightedIds={dragHighlightedIds}
                            isDragActive={isDragging}
                          />
                        </InfiniteScrollContainer>

                        {/* For small screens, below 840px(m2 breakpoints) */}
                        <div className="m2:display-block display-none">
                          {categories.length > 0 ? (
                            <div className="library-view__accordion">
                              {categories.map((category) => (
                              <ZprAccordion className="no-background" key={category}>
                                <ZprAccordionPanel onPanelOpened={() => setOpenedCategories(prev => new Set(prev).add(category))}>
                                  <span slot="accordion-header">{category.charAt(0).toUpperCase() + category.slice(1)}</span>
                                  <div slot="accordion-body" className="library-view__accordion-body">
                                    {openedCategories.has(category) && (
                                      <CategoryScrollSection
                                        mediaAssetsParams={{
                                          registry,
                                          mediaType: filterMediaType || enabledMediaTypes || undefined,
                                          source: activeProvider,
                                          searchQuery,
                                          sortBy,
                                          filters,
                                          refreshKey,
                                        }}
                                        category={category}
                                        onAssetClick={handleAssetClick}
                                        onAssetSelect={toggleSelection}
                                        selectedAssets={selectedAssetIds}
                                        dragHighlightedIds={dragHighlightedIds}
                                        isDragActive={isDragging}
                                        onAssetsChange={handleCategoryAssetsChange}
                                      />
                                    )}
                                  </div>
                                </ZprAccordionPanel>
                              </ZprAccordion>
                              ))}
                            </div>
                          ) :
                          (
                            <InfiniteScrollContainer
                              onLoadMore={activeProviderState?.loadMore ?? (() => {})}
                              hasMore={activeProviderState?.hasMore ?? false}
                              loadingMore={activeProviderState?.loadingMore ?? false}
                              loader={<div className="library-view__loading"><ProcessingIcon /></div>}
                              error={!!activeProviderState?.error}
                            >
                              <MediaGrid
                                assets={activeProviderState?.assets || []}
                                onAssetClick={handleAssetClick}
                                onAssetSelect={toggleSelection}
                                selectedAssets={selectedAssetIds}
                                dragHighlightedIds={dragHighlightedIds}
                                isDragActive={isDragging}
                              />
                            </InfiniteScrollContainer>
                          )}
                        </div>
                        </>
                      ) : (
                        providerAssets.map((provider) => (
                          <AssetRow
                            key={provider.providerId}
                            title={provider.providerName}
                            // count={provider.state.count}
                            state={provider.state}
                            enableMultiSelect={enableMultiSelect}
                            onAssetClick={handleAssetClick}
                            onAssetSelect={toggleSelection}
                            onViewAll={() => handleViewAll(provider.providerId)}
                            selectedAssets={selectedAssetIds}
                            dragHighlightedIds={dragHighlightedIds}
                          />
                        ))
                      )}

                    </>
                  )}
              </div>
            </Dropzone>
          )}
        </DragSelectContainer>
      </div>

      <AssetDetailPanel
        selectedAssets={selectedAssetObjects}
        selectedAssetIds={selectedAssetIds}
        totalSelectedCount={selectedAssetIds.length}
        hiddenSelectedCount={hiddenSelectedCount}
        activeSource={activeSource || 'explore'}
        onDelete={handleDeleteAsset}
        onDeleteMultiple={handleDeleteMultiple}
        onDeselectAll={clearSelection}
        disableEditName={true}
        isExploreView={true}
        key={selectedAssetIds.join(',')} // Force remount when selection changes to reset internal state
        mediaLibraryContainerRef={containerRef}
        onEdit={activeProvider ? onEdit : undefined}
      />
    </div>
  );
};
